// Wire-protocol vocabulary shared by client and server. Plain types and
// constants only — no Colyseus imports here; schema classes live server-side.

export const PROTOCOL_VERSION = 2;

export const ROOM_NAME_HUB = "hub";

/** Client → server: hub transform snapshot (client-predicted hub movement). */
export const HUB_MOVE_MESSAGE = "hub:move";
/** Dev-only: ask the server to maintain N wandering sim players. */
export const HUB_SET_SIMS_MESSAGE = "hub:debug_set_sims";
/** Client → server: the player's look changed after joining (a new outfit, or
 *  the account's loadout arriving late); the room republishes cosmeticsJson. */
export const HUB_COSMETICS_MESSAGE = "hub:cosmetics";

export type HubCosmeticsPayload = {
  cosmeticsJson: string;
};

/** The longest cosmeticsJson a room keeps: room for a full outfit and a skin
 *  in every weapon slot (DR-0060), which runs past 500 characters. */
export const COSMETICS_JSON_MAX = 1024;

/** Client → server → everyone else in the hub: what a player's body just did,
 *  so their avatar does it too (positions still come from hub:move). */
export const HUB_ACTION_MESSAGE = "hub:action";
export const HUB_ACTIONS = ["jump", "fall", "land", "mantle"] as const;
export type HubAction = (typeof HUB_ACTIONS)[number];

export type HubActionPayload = {
  action: HubAction;
  /** mantle: the clip (the hub's clip ids) and its playback rate. */
  clip?: string;
  rate?: number;
  /** land: how fast the body came down (m/s). */
  fallSpeed?: number;
};

export type HubActionRelayPayload = HubActionPayload & { sessionId: string };

export type HubMovePayload = {
  x: number;
  y: number;
  z: number;
  yaw: number;
};

export type HubJoinOptions = {
  playerId: string;
  displayName: string;
  cosmeticsJson?: string;
  /** Supabase session JWT (Foundations F1). The server verifies it in onAuth and
   *  derives identity from it; `playerId` is a legacy fallback during rollout. */
  authToken?: string;
  protocolVersion: number;
};

/** Plain mirror of the server's HubPlayer schema for client-side typing. */
export type HubPlayerSnapshot = {
  playerId: string;
  displayName: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  cosmeticsJson: string;
  isSim: boolean;
};

export const HUB_MAX_PLAYERS = 24;
/** Hub presence prioritises liveliness over precision (master plan §15). */
export const HUB_MOVE_SEND_HZ = 10;
/** Server-side clamp for hub movement: the hub model's floor fits inside, as on
 *  the client (it was the old 14 m blockout's, which pinned players at its edge). */
export const HUB_BOUNDS_HALF_EXTENT = 60;
/** And for height (feet, m): mantles reach the hub's rooftops. */
export const HUB_HEIGHT_RANGE = [-20, 60] as const;

// ---------------------------------------------------------------------------
// Wave mode (packet 011): WaveLobbyRoom + WaveGameRoom, ClientHostAuthority
// (master plan §12.2/§12.5). The server relays and keeps durable room state;
// the host client owns enemy AI, wave state, spawning and damage validation.
// ---------------------------------------------------------------------------

import type { Vec3 } from "./math";
import type { WavePhase } from "./waves";
import type { UpgradeCard } from "./upgrades";
import type { RankedCard } from "../logic/rankedCards";

export const ROOM_NAME_WAVE_LOBBY = "wave_lobby";
export const ROOM_NAME_WAVE_GAME = "wave_game";
/** Ludus (the auto-battler): one room is the lobby and then the match; filtered by mode (casual/ranked). */
export const ROOM_NAME_LUDUS = "ludus";
/** A party-follow code for a Ludus table: this prefix, then the table's room id (social set_location). */
export const LUDUS_PARTY_CODE_PREFIX = "ludus:";
/** Ground War (ACT HQ DR-0020): one room per playlist match, filtered by playlist id (@act/groundwar-sim's table). */
export const ROOM_NAME_GROUNDWAR = "groundwar";
/** A party-follow code for a Ground War match: this prefix, then the room id (a private match is joined by id). */
export const GROUNDWAR_PARTY_CODE_PREFIX = "groundwar:";
/** Vector Drift's online races (ACT HQ t-492, act-babylon DR-0094): one room per race, home server only. */
export const ROOM_NAME_VECTORDRIFT = "vectordrift";

/**
 * Enemy-AI mode for a run (PLAY P3), chosen host-side at the ready-up lobby and
 * carried lobby→game like any run-setup value. "odst" = the tactical squad AI;
 * "zombie" = a relentless beeline-melee swarm that routes around the structure.
 * The first real run-config handoff — the model TUNE-T5 / per-map clamps follow.
 */
export type AiMode = "odst" | "zombie";
export const DEFAULT_AI_MODE: AiMode = "odst";

/**
 * Run mode (ACT HQ DR-0013, t-552/t-553): each is a wave schedule in the game's
 * tuning. "arcade" is a set run that ends in a win (the "won" phase);
 * "endless" repeats and gets harder until the team is out of lives. Host-chosen
 * in the lobby like the AI mode, and carried to the run in WaveStartPayload.
 */
export type RunMode = "arcade" | "endless";
export const DEFAULT_RUN_MODE: RunMode = "endless";

// ---- Lobby ----

/** Client → server: toggle own ready state. */
export const LOBBY_SET_READY_MESSAGE = "lobby:set_ready";
/** Host → server: pick the run's enemy-AI mode (PLAY P3); guests' sends ignored. */
export const LOBBY_SET_AI_MODE_MESSAGE = "lobby:set_ai_mode";
/** Host → server: toggle the card-slot upgrade mode (packet 038); guests ignored. */
export const LOBBY_SET_CARDS_ENABLED_MESSAGE = "lobby:set_cards_enabled";
/** Host → server: pick the run mode (arcade or endless); guests' sends ignored. */
export const LOBBY_SET_RUN_MODE_MESSAGE = "lobby:set_run_mode";
/** Server → one client: your reserved seat in the created WaveGameRoom. */
export const LOBBY_GAME_READY_MESSAGE = "lobby:game_ready";

/** Who runs a wave run: a host player's browser (DR-0001), or the game server
 *  itself (ACT HQ DR-0019: server authority, for ranked play and tournaments). */
export type WaveAuthority = "client_host" | "server";

export type WaveLobbyJoinOptions = {
  playerId: string;
  displayName: string;
  /** Carried through the lobby into the game room (avatar tint). */
  cosmeticsJson?: string;
  /** Supabase session JWT (Foundations F1) — verified in onAuth; the verified
   *  identity is what the lobby reserves the game seat with. */
  authToken?: string;
  /** NET N1: create this lobby private (invite-code only, hidden from public
   *  Quick-Play matchmaking). Only honoured when the room is freshly created
   *  (client.create); ignored when joining an existing lobby by id/matchmaking. */
  isPrivate?: boolean;
  /** DR-0019: the run this lobby starts is hosted by the server, every player a
   *  guest. Absent = a host player's browser (client_host). Only honoured on create;
   *  Quick Play matches only lobbies of the same authority and rank. */
  authority?: WaveAuthority;
  /** DR-0019: a ranked run (server-hosted only), written to the scoreboards. Ranked
   *  lobbies take signed-in accounts only. Bots and casual rooms never set it. */
  ranked?: boolean;
  /** DR-0019: a one-player lobby (Solo): private, nobody else can join. */
  solo?: boolean;
  /** DR-0019: an event's mode (FIRESTRIKE_EVENTS, e.g. "basecamp"): server-hosted, ranked,
   *  the event's own rules; refused outside its window. */
  event?: string;
  protocolVersion: number;
};

export type LobbySetReadyPayload = {
  ready: boolean;
};

/** Host picks the run's enemy-AI mode (PLAY P3). Server enforces host-only. */
export type LobbySetAiModePayload = {
  aiMode: AiMode;
};

/** Host toggles card-slot upgrade mode (packet 038). Server enforces host-only. */
export type LobbySetCardsEnabledPayload = {
  cardsEnabled: boolean;
};

/** Host picks the run mode. Server enforces host-only. */
export type LobbySetRunModePayload = {
  runMode: RunMode;
};

export type LobbyGameReadyPayload = {
  /** Opaque Colyseus seat reservation — consumed by @colyseus/sdk as-is. */
  reservation: unknown;
  isHost: boolean;
};

/** Plain mirror of the server's LobbyPlayer schema. */
export type LobbyPlayerSnapshot = {
  playerId: string;
  displayName: string;
  ready: boolean;
  joinOrder: number;
};

export type WaveLobbyPhase = "gathering" | "countdown" | "starting";

// ---- Wave game room lifecycle ----

/**
 * Server-owned room phase. Gameplay phases (WavePhase) are host-owned and
 * travel via WAVE_HOST_STATE_MESSAGE; this only tracks membership lifecycle.
 */
export type WaveRoomPhase = "gathering" | "running" | "aborted";

/** Server → all: everyone reserved has joined (or gather timed out). */
export const WAVE_START_MESSAGE = "wave:start";
/** Server → all: run is over without results (host left, errors). */
export const WAVE_RUN_ABORTED_MESSAGE = "wave:run_aborted";
/** Server → all: the host left mid-run; authority migrated to a new player (NET-H2). */
export const WAVE_HOST_MIGRATED_MESSAGE = "wave:host_migrated";

export type WaveGameJoinOptions = {
  playerId: string;
  displayName: string;
  cosmeticsJson?: string;
  protocolVersion: number;
};

export type WaveStartPayload = {
  playerIds: string[];
  hostPlayerId: string;
  /** Shared team lives pool for this run (master plan §13.3). */
  livesPool: number;
  /** Enemy-AI mode chosen by the host at the lobby (PLAY P3). */
  aiMode: AiMode;
  /** Card-slot upgrade mode on for this run (packet 038); host-chosen at lobby. */
  cardsEnabled: boolean;
  /** Arcade or endless, host-chosen at the lobby; absent from older servers (= endless). */
  runMode?: RunMode;
};

export type WaveRunAbortReason = "host_left" | "host_never_joined" | "server_error";

export type WaveRunAbortedPayload = {
  reason: WaveRunAbortReason;
};

/** Server → all on a host migration (NET-H2). If `newHostPlayerId` is you, take
 *  over the authority (reconstruct it from the synced room state). */
export type WaveHostMigratedPayload = {
  newHostPlayerId: string;
};

// ---- Wave game: client → server ----

/** Any client → server: own transform (server schema; host AI targets it). */
export const WAVE_MOVE_MESSAGE = "wave:move";
/** Non-host → server → host: fire request; host re-traces and validates. */
export const WAVE_SHOT_REQUEST_MESSAGE = "wave:shot_request";

export type WaveMovePayload = {
  x: number;
  y: number;
  z: number;
  yaw: number;
};

/**
 * Fire request: full ray + pelletSeed so the host re-traces the exact shot
 * (the packet-006 seeded-spread contract), at the moment the shooter saw
 * (`viewTimeMs`, lag compensation). Never carries claimed hits or damage —
 * the host decides those (§12.5).
 */
export type WaveShotRequestPayload = {
  weaponId: string;
  origin: Vec3;
  direction: Vec3;
  /** uint32; regenerates the pellet fan for spread weapons. */
  pelletSeed: number;
  /** Shooter-local increasing id so results correlate to requests. */
  clientShotId: number;
  /** Host sim time (ms) the shooter's screen showed when it fired, worked out
   *  from the enemy snapshots' `t`. The host re-traces the same ray against
   *  enemies rewound to that moment (lag compensation, DR-0049), at most
   *  WAVE_MAX_REWIND_MS back. Absent from older clients: no rewind. */
  viewTimeMs?: number;
};

/** Server → host: a validated shot request stamped with the sender. */
export type WaveShotRequestRelayPayload = WaveShotRequestPayload & {
  shooterPlayerId: string;
};

// ---- Wave game: host → server ----

/** Host → server: durable run state applied onto the room schema. */
export const WAVE_HOST_STATE_MESSAGE = "wave:host_state";
/** Host → server → others: hot enemy transforms (10 Hz, not schema, §12.4). */
export const WAVE_ENEMY_SNAPSHOT_MESSAGE = "wave:enemy_snapshot";
/** Host → server → the shooter: adjudicated result of one shot request. */
export const WAVE_SHOT_RESULT_MESSAGE = "wave:shot_result";
/** Host → server → all: a player took damage (host owns player vitals). */
export const WAVE_PLAYER_DAMAGE_MESSAGE = "wave:player_damage";
/** Host → server → all: a player respawned at a host-chosen position. */
export const WAVE_PLAYER_RESPAWN_MESSAGE = "wave:player_respawn";
/** Host → server → all: final run results (always UNRANKED, §12.5/§16). */
export const WAVE_RUN_RESULTS_MESSAGE = "wave:run_results";
/** Host → server → others: an enemy fired at a player (tracer/flash echo). */
export const WAVE_ENEMY_ATTACK_MESSAGE = "wave:enemy_attack";

export type WaveEnemyAttackPayload = {
  entityId: string;
  targetPlayerId: string;
  /** Whether the shot landed (miss tracers deliberately go wide). */
  hit: boolean;
  /** Attack presentation (packet 046): absent/"shot" → tracer echo; "melee" →
   *  the windup telegraph (replicas play the melee clip, no tracer). */
  kind?: "shot" | "melee";
};

/** Any player → server → all: a map ping (PLAY P1). The client sends only the
 *  world point; the server stamps the sender's playerId (never trust client
 *  identity, §16) and relays. Sender shows its own ping locally on press. */
export const WAVE_PLAYER_PING_MESSAGE = "wave:player_ping";

export type WavePlayerPingPayload = {
  playerId: string;
  x: number;
  y: number;
  z: number;
};

/** Any player → server → all: a one-shot avatar action (FIX-001 follow-up) so a
 *  remote player's shoot/melee reads on everyone's screen. The client sends only
 *  the action; the server stamps the sender's playerId. "hit"/"death" are driven
 *  by the existing damage/alive signals, not this message. */
export const WAVE_PLAYER_ACTION_MESSAGE = "wave:player_action";

export type WavePlayerActionKind = "shoot" | "melee" | "throw" | "reload";

export type WavePlayerActionPayload = {
  playerId: string;
  action: WavePlayerActionKind;
};

/** Any player → server: the weapon now in their hands (on joining and on every
 *  switch). The room keeps it on the player's schema (`weaponId`), so everyone,
 *  late joiners included, sees them holding it. */
export const WAVE_PLAYER_WEAPON_MESSAGE = "wave:player_weapon";

export type WavePlayerWeaponPayload = {
  weaponId: string;
};

/** Any player → server → host: a thrown-grenade request (PLAY P2). Like a shot
 *  request (§12.5), the host owns the authoritative arc + detonation; the client
 *  sends only origin + aim and predicts the throw visual. The server stamps the
 *  sender's throwerPlayerId — the client never asserts identity. */
export const WAVE_GRENADE_REQUEST_MESSAGE = "wave:grenade_request";

export type WaveGrenadeRequestPayload = {
  origin: Vec3;
  direction: Vec3;
  /** Thrower-local increasing id (correlates a prediction to its host echo). */
  clientGrenadeId: number;
};

/** Server → host: a validated grenade request stamped with the sender. */
export type WaveGrenadeRequestRelayPayload = WaveGrenadeRequestPayload & {
  throwerPlayerId: string;
};

/** Non-host → server → host: a card the player picked at a wave-clear (packet
 *  038). The host owns each player's modifiers (it scales their damage/vitals/
 *  grenades), so a guest reports its pick and the host applies the canonical
 *  magnitude — the client picks WHICH card, never the numbers. The host applies
 *  its own pick locally and never sends this. */
export const WAVE_CARD_PICK_MESSAGE = "wave:card_pick";

export type WaveCardPickPayload = {
  card: UpgradeCard;
  /** In a scored run, the server's offer this picks from (WaveCardOfferPayload.offerId); only the card's effect counts. */
  offerId?: number;
};

/** Server → each player, at a wave clear in a scored, server-hosted run (Theo, 2 Oct 2026: the cards "should also be
 *  server side"): the cards on offer, dealt by the server, always the same three (logic/rankedCards), worth double
 *  after a boss round. The player answers with WAVE_CARD_PICK_MESSAGE carrying the offerId and one of these effects;
 *  the server applies the bonus it dealt, once per offer. */
export const WAVE_CARD_OFFER_MESSAGE = "wave:card_offer";

export type WaveCardOfferPayload = {
  offerId: number;
  /** The wave just cleared was a boss round, so every card is worth double. */
  boss: boolean;
  cards: RankedCard[];
  /** How long the pick stays open (ms). */
  pickWindowMs: number;
};

/** Server → host: a validated card pick stamped with the sender. */
export type WaveCardPickRelayPayload = {
  playerId: string;
  card: UpgradeCard;
  /** In a server-hosted scored run, the server's offer the pick is from (WaveCardPickPayload.offerId). */
  offerId?: number;
};

/** Host → server → all others: an authoritative grenade detonation (PLAY P2).
 *  Carries only the blast point for the networked explosion VFX; the actual
 *  AoE damage/kills travel via the existing wave:player_damage + enemy-snapshot
 *  paths. The thrower already showed the boom locally (its fuse hides the RTT). */
export const WAVE_GRENADE_DETONATION_MESSAGE = "wave:grenade_detonation";

export type WaveGrenadeDetonationPayload = {
  throwerPlayerId: string;
  x: number;
  y: number;
  z: number;
};

export type WaveHostPlayerState = {
  playerId: string;
  health: number;
  shield: number;
  alive: boolean;
  kills: number;
  deaths: number;
  /** Grenades remaining (PLAY P2); host-owned, refilled at each wave-clear. */
  grenades: number;
};

export type WaveHostStatePayload = {
  wavePhase: WavePhase;
  waveIndex: number;
  livesPool: number;
  enemiesLeft: number;
  teamScore: number;
  players: WaveHostPlayerState[];
};

export type WaveEnemySnapshot = {
  entityId: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** 0..1 of max health (future health bars); 0 when dead. */
  healthFrac: number;
  alive: boolean;
  /** Up a wall (a robot climbing to a player on high ground): its body turned to lie against the wall,
   *  playing the climb. Absent when not (and from older servers). */
  climbing?: boolean;
};

export type WaveEnemySnapshotPayload = {
  enemies: WaveEnemySnapshot[];
  /** Host sim time (ms) when these transforms were taken. Guests turn it into
   *  a shot's `viewTimeMs` (lag compensation). Absent from older hosts. */
  t?: number;
};

export type WaveShotKill = {
  entityId: string;
  isHeadshot: boolean;
  points: number;
  medal: string | null;
  medalBonus: number;
};

export type WaveShotResultPayload = {
  shooterPlayerId: string;
  clientShotId: number;
  hit: boolean;
  headshot: boolean;
  kills: WaveShotKill[];
};

export type WavePlayerDamagePayload = {
  playerId: string;
  amount: number;
  health: number;
  shield: number;
  alive: boolean;
  /** Set when this damage killed the player and a respawn is scheduled. */
  respawnInMs: number | null;
};

export type WavePlayerRespawnPayload = {
  playerId: string;
  position: Vec3;
};

export type WaveRunPlayerResult = {
  playerId: string;
  displayName: string;
  kills: number;
  headshotKills: number;
  deaths: number;
  bestChain: number;
  score: number;
};

export type WaveRunResultsPayload = {
  /** Client-host runs never feed trusted public scores (§12.5); server-hosted
   *  ranked runs do (DR-0019). */
  authorityMode: WaveAuthority;
  /** A server-hosted run that counts for the scoreboards (DR-0019). */
  ranked?: boolean;
  /** An arcade run that cleared its last wave (DR-0013); absent = not won. */
  won?: boolean;
  wavesCompleted: number;
  teamScore: number;
  players: WaveRunPlayerResult[];
};

/** Plain mirror of the server's WavePlayer schema for client-side typing. */
export type WavePlayerSnapshot = {
  playerId: string;
  displayName: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  health: number;
  shield: number;
  alive: boolean;
  kills: number;
  deaths: number;
  grenades: number;
  isHost: boolean;
  cosmeticsJson: string;
  /** The weapon in their hands (`wave:player_weapon`); "" until they've said. */
  weaponId: string;
  /** Server-owned (NET N2): false while this player is inside a reconnection
   *  window (an unexpected drop mid-run). Drives the "reconnecting" UI; the host
   *  never writes it via host_state — only the server's drop/reconnect knows. */
  connected: boolean;
};

// ---- Wave constants ----

export const WAVE_MAX_PLAYERS = 4;
/** Combat positions want a little more freshness than hub presence. */
export const WAVE_MOVE_SEND_HZ = 15;
export const WAVE_ENEMY_SNAPSHOT_HZ = 10;
export const WAVE_HOST_STATE_HZ = 5;
export const LOBBY_COUNTDOWN_MS = 3000;
/** Gathering closes early once everyone reserved has joined. */
export const WAVE_GATHER_TIMEOUT_MS = 8000;
/** No host within this window after room creation → abort (fail-safe). */
export const WAVE_HOST_JOIN_TIMEOUT_MS = 10000;
/**
 * Server-side anti-teleport sanity bound for replicated wave positions (master
 * plan §11 — "reject impossible positions/teleporting"). NOT a tight per-arena
 * fit: the host's mesh collision is the real bounds authority. It must cover the
 * LARGEST supported arena with margin so legitimate roaming isn't clamped —
 * Station 17's half-extent is 80 (web STATION_HALF_EXTENT). FIX-003: the old
 * blockout-era value of 19 pinned teammates to a 19 m box on the 80 m station,
 * which read as a constant positional offset (the hub, ~19 m, was unaffected).
 */
export const WAVE_ARENA_HALF_EXTENT = 100;
/** The same bound up and down (m). Players reach Station 17's 32 m roof from its pads (the one on the
 *  18.5 m roof throws them to about 36 m). The old ceiling of 30 m kept such a player's feet below them
 *  while their shots came from where they stood, so the server refused every shot from up there (t-872:
 *  Robbos and energicon in Basecamp), and the robots never climbed up to them. */
export const WAVE_ARENA_MIN_Y = -5;
export const WAVE_ARENA_MAX_Y = 100;
/** Host-side plausibility clamp: shot origin vs sender's replicated position. */
export const WAVE_SHOT_ORIGIN_TOLERANCE_M = 3;
/** Lag compensation: the furthest back the host rewinds enemies for a shot. */
export const WAVE_MAX_REWIND_MS = 1000;
/** How long a map ping (PLAY P1) stays visible — "a few seconds". */
export const WAVE_PING_LIFETIME_MS = 5000;
/**
 * NET N2: reconnection window. On an unexpected mid-run drop the server holds
 * the player's seat (and the host's authority) for this long; the client retries
 * `client.reconnect()` with backoff until then. Long enough to ride out a phone
 * backgrounding / wifi blip, short enough that a truly-gone host ends the run
 * promptly. allowReconnection() takes seconds, so this is in seconds.
 */
export const WAVE_RECONNECT_WINDOW_SECONDS = 20;

// ---- Firefight round structure (ODST rhythm, packet 013) ----

export const WAVES_PER_ROUND = 5;

/** 1-based round for a 1-based global wave index. */
export function roundForWave(waveIndex: number): number {
  return Math.floor((Math.max(1, waveIndex) - 1) / WAVES_PER_ROUND) + 1;
}

/** 1-based position of a wave inside its round (1..WAVES_PER_ROUND). */
export function waveInRound(waveIndex: number): number {
  return ((Math.max(1, waveIndex) - 1) % WAVES_PER_ROUND) + 1;
}

export function isRoundFinalWave(waveIndex: number): boolean {
  return waveInRound(waveIndex) === WAVES_PER_ROUND;
}

const WAVE_BASE_SHARED_LIVES = 2;
/** Arcade's extra lives over endless, like an arcade cabinet (Theo's board: 5 to
 *  start solo). The server can't read a game's tuning, so it's a constant. */
export const ARCADE_EXTRA_LIVES = 2;

/** Shared team lives pool: 3 solo (matches single-player), +1 per extra player;
 *  arcade adds ARCADE_EXTRA_LIVES. */
export function sharedLivesForPlayerCount(playerCount: number, runMode: RunMode = DEFAULT_RUN_MODE): number {
  const base = WAVE_BASE_SHARED_LIVES + Math.max(1, Math.min(WAVE_MAX_PLAYERS, Math.floor(playerCount)));
  return base + (runMode === "arcade" ? ARCADE_EXTRA_LIVES : 0);
}
